import type { ReactNode } from 'react';
import { ArrowRightRegular, CheckmarkCircleRegular, WarningRegular } from '@fluentui/react-icons';

export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="eyebrow">{children}</div>;
}

export function StatusBadge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: string;
}) {
  return <span className={`status-badge ${tone}`}>{children}</span>;
}

export function CoverageBadge({ state }: { state: string }) {
  const normalized = state.toLowerCase();
  const Icon =
    normalized.includes('complete') || normalized.includes('approved')
      ? CheckmarkCircleRegular
      : WarningRegular;
  return (
    <span className={`coverage-badge ${normalized}`}>
      <Icon aria-hidden /> {state.replaceAll('_', ' ')}
    </span>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function MetricCard({
  label,
  value,
  meta,
  tone = 'teal',
}: {
  label: string;
  value: string | number;
  meta: string;
  tone?: string;
}) {
  return (
    <article className={`metric-card ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{meta}</small>
    </article>
  );
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty-state">
      <div className="empty-mark">◇</div>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

export function LoadingState({ label = 'Loading current evidence' }: { label?: string }) {
  return (
    <div className="loading-state" role="status">
      <span className="spinner" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorState({ error }: { error: Error }) {
  return (
    <div className="notice error" role="alert">
      <WarningRegular aria-hidden />
      <div>
        <strong>We could not load this view.</strong>
        <p>{error.message}</p>
      </div>
    </div>
  );
}

export function SectionTitle({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="section-title">
      <h2>{title}</h2>
      {action}
    </div>
  );
}

export function ExplanationPath({ items }: { items: string[] }) {
  return (
    <ol className="explanation-path" aria-label="Relationship explanation">
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>
          <span>{item}</span>
          {index < items.length - 1 ? <ArrowRightRegular aria-hidden /> : null}
        </li>
      ))}
    </ol>
  );
}
